import { getCurrentInstance, nextTick, onBeforeUnmount, reactive } from 'vue'
import { watchDebounced } from '@vueuse/core'

export type PreviewOverflowWatchSource = {
  name: string
  source: () => unknown
}

type HostRefs = Record<string, unknown>

// 枠に収まらないときに許容する最小フォントサイズ（8pt 相当）。
// これ以上は縮小せず、エラー表示に切り替える。
const MIN_FONT_SIZE_PX = 11
// getComputedStyle が値を返さない環境（テスト等）での既定値（11pt 相当）
const FALLBACK_FONT_SIZE_PX = 15
// 収まる最大サイズを探すときの刻み幅
const FONT_SIZE_STEP_PX = 0.5

function asMeasureEl(el: unknown): HTMLElement | undefined {
  if (el == null || typeof el !== 'object') return undefined
  const node = el as {
    clientHeight?: unknown
    scrollHeight?: unknown
    $el?: unknown
  }
  if (
    typeof node.clientHeight === 'number' &&
    typeof node.scrollHeight === 'number'
  ) {
    return el as HTMLElement
  }
  if (node.$el) return asMeasureEl(node.$el)
  return undefined
}

function resolveMeasureEl(el: unknown): HTMLElement | undefined {
  if (Array.isArray(el)) {
    for (const item of el) {
      const measured = resolveMeasureEl(item)
      if (measured) return measured
    }
    return undefined
  }
  return asMeasureEl(el)
}

function overflowHeight(el: HTMLElement): number {
  return Math.max(el.clientHeight, el.scrollHeight)
}

function baseFontSizeOf(el: HTMLElement): number {
  try {
    if (typeof window === 'undefined' || !window.getComputedStyle) {
      return FALLBACK_FONT_SIZE_PX
    }
    const size = parseFloat(window.getComputedStyle(el).fontSize)
    return Number.isFinite(size) && size > 0 ? size : FALLBACK_FONT_SIZE_PX
  } catch (e) {
    return FALLBACK_FONT_SIZE_PX
  }
}

export function usePreviewOverflow(options: {
  getEl: (name: string) => unknown
  maxHeights: Record<string, number>
  watchSources?: PreviewOverflowWatchSource[]
}) {
  const classObject = reactive<Record<string, Record<string, boolean>>>({})
  const errorMessages = reactive<Record<string, string>>({})
  const instance = getCurrentInstance()
  // 監視中の要素も持つ。v-if で要素が差し替わったら張り直すため
  const observers = new Map<
    string,
    { observer: ResizeObserver; el: HTMLElement }
  >()

  function ensureKeys(name: string) {
    if (!(name in classObject)) {
      classObject[name] = {}
    }
    if (!(name in errorMessages)) {
      errorMessages[name] = ''
    }
  }

  for (const name of Object.keys(options.maxHeights)) {
    ensureKeys(name)
  }

  function getMeasureEl(refName: string): HTMLElement | undefined {
    const fromGetter = resolveMeasureEl(options.getEl(refName))
    if (fromGetter) return fromGetter
    const refs = instance?.proxy?.$refs as HostRefs | undefined
    return resolveMeasureEl(refs?.[refName])
  }

  function setFontSize(el: HTMLElement, px: number | null) {
    if (!el.style) return
    const next = px == null ? '' : `${px}px`
    // 同じ値なら書き換えない。ResizeObserver から呼ばれたときに再発火させないため
    if (el.style.fontSize === next) return
    if (px == null && el.style.removeProperty) {
      el.style.removeProperty('font-size')
      return
    }
    el.style.fontSize = next
  }

  // classObject の反映は Vue の次回描画待ちになるため、PDF 送信用に
  // innerHTML を同期で取れるよう DOM にも直接当てる
  function setErrorClass(el: HTMLElement, on: boolean) {
    if (!el.classList) return
    if (on) el.classList.add('error')
    else el.classList.remove('error')
  }

  // DOM 更新後に同期で測って確定させる。PDF 送信直前にも呼べるよう nextTick を挟まない
  function adjustFontSizeSync(refName: string) {
    ensureKeys(refName)
    const maxHeight = options.maxHeights[refName]
    if (maxHeight == null) return

    // v-if で計測要素が生成／破棄されるので、判定のたびに監視対象を追従させる
    observeElement(refName)

    const el = getMeasureEl(refName)
    if (!el) {
      // 要素が消えたら以前のエラー表示を残さない
      classObject[refName] = {}
      errorMessages[refName] = ''
      return
    }

    // 基準サイズに戻して素の高さを測る
    setFontSize(el, null)
    setErrorClass(el, false)
    classObject[refName] = {}
    errorMessages[refName] = ''

    const baseFontSize = baseFontSizeOf(el)
    const naturalHeight = overflowHeight(el)
    if (naturalHeight <= maxHeight) {
      return
    }

    // 高さの比から当たりを付ける。ただし折り返しがあるぶん高さは font-size に
    // 単純比例しないので、この値は縮めすぎになる
    const estimated =
      Math.floor(baseFontSize * (maxHeight / naturalHeight) * 10) / 10
    let applied = Math.max(MIN_FONT_SIZE_PX, Math.min(baseFontSize, estimated))
    setFontSize(el, applied)

    // 実測しながら、枠に収まる最大のサイズまで戻す
    if (overflowHeight(el) <= maxHeight) {
      while (applied < baseFontSize) {
        const next = Math.min(baseFontSize, applied + FONT_SIZE_STEP_PX)
        setFontSize(el, next)
        if (overflowHeight(el) > maxHeight) {
          setFontSize(el, applied)
          break
        }
        applied = next
      }
    }

    if (overflowHeight(el) > maxHeight) {
      errorMessages[refName] = '入力欄をオーバーしてます'
      classObject[refName] = { error: true }
      setErrorClass(el, true)
    }
  }

  function adjustFontSize(refName: string) {
    ensureKeys(refName)
    nextTick(() => {
      adjustFontSizeSync(refName)
    })
  }

  // v-show でのタブ切替やフォント読み込み完了など、watch では拾えない
  // レイアウト変化でも判定が走るようにする
  function observeElement(refName: string) {
    if (typeof ResizeObserver === 'undefined') return
    const el = getMeasureEl(refName)
    const current = observers.get(refName)
    if (current && current.el === el) return
    if (current) {
      current.observer.disconnect()
      observers.delete(refName)
    }
    if (!el || typeof el.getBoundingClientRect !== 'function') return
    const observer = new ResizeObserver(() => {
      adjustFontSizeSync(refName)
    })
    observer.observe(el)
    observers.set(refName, { observer, el })
  }

  function observeAll() {
    for (const name of Object.keys(options.maxHeights)) {
      observeElement(name)
    }
  }

  if (options.watchSources) {
    for (const item of options.watchSources) {
      watchDebounced(
        item.source,
        () => {
          adjustFontSize(item.name)
        },
        { debounce: 500 }
      )
    }
  }

  if (instance) {
    try {
      onBeforeUnmount(() => {
        for (const entry of observers.values()) {
          entry.observer.disconnect()
        }
        observers.clear()
      })
    } catch (e) {
      // setup() 外から呼ばれた場合はライフサイクルに載せられないので何もしない
    }
  }

  return {
    classObject,
    errorMessages,
    adjustFontSize,
    adjustFontSizeSync,
    observeAll
  }
}
